
import React, { useState, useEffect } from 'react';
import { MapPin, Users, GripVertical, Map as MapIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const Step6RouteOrder = ({ wizardData = {}, updateWizardData }) => {
  const [activeTab, setActiveTab] = useState('append');
  const [routeStops, setRouteStops] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);

  // Ensure wizardData has required properties with defaults
  const safeWizardData = {
    selectedStudents: [],
    sourceBuses: [],
    routeOrder: [],
    ...wizardData,
  };

  useEffect(() => {
    // Generate mock stops from selected students
    const stops = [];
    const stopMap = new Map();

    safeWizardData.selectedStudents?.forEach((student) => {
      if (!student || !student.busId || !student.stop || !student.busNumber) {
        return;
      }
      
      const key = `${student.busId}-${student.stop}`;
      if (!stopMap.has(key)) {
        stopMap.set(key, {
          id: key,
          name: student.stop,
          busNumber: student.busNumber,
          busId: student.busId,
          studentCount: 1,
          students: [student],
        });
      } else {
        const stop = stopMap.get(key);
        stop.studentCount++;
        stop.students.push(student);
      }
    });

    const stopsArray = Array.from(stopMap.values());
    setRouteStops(stopsArray);

    // Auto-save route order
    if (!safeWizardData.routeOrder || safeWizardData.routeOrder.length === 0) {
      updateWizardData('routeOrder', stopsArray);
    }
  }, [safeWizardData.selectedStudents]);

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newStops = [...routeStops];
    const draggedStop = newStops[draggedItem];
    newStops.splice(draggedItem, 1);
    newStops.splice(index, 0, draggedStop);

    setRouteStops(newStops);
    setDraggedItem(index);
    updateWizardData('routeOrder', newStops);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const getBusColor = (busId) => {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500'];
    
    if (!safeWizardData.sourceBuses || safeWizardData.sourceBuses.length === 0) {
      return colors[0];
    }
    
    const index = safeWizardData.sourceBuses.findIndex((bus) => bus.id === busId);
    return colors[index >= 0 ? index % colors.length : 0];
  };

  const totalStudents = routeStops.reduce((sum, stop) => sum + stop.studentCount, 0);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-600 mb-2">
          Organize the route stops for the merged bus. You can append stops in their original order
          or manually reorder them for optimal routing.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="append">Append In Order</TabsTrigger>
          <TabsTrigger value="manual">Manual Reorder</TabsTrigger>
        </TabsList>

        <TabsContent value="append" className="space-y-4 mt-6">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              Stops will be appended in the order of the source buses. This maintains the existing
              stop sequence from each bus.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Stops List */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">Route Stops ({routeStops.length})</h3>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {routeStops.map((stop, index) => (
                  <Card key={stop.id} className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-sm font-semibold text-gray-700">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <MapPin className="h-4 w-4 text-gray-500" />
                          <h4 className="font-medium text-gray-900">{stop.name}</h4>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {stop.studentCount} {stop.studentCount === 1 ? 'student' : 'students'}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white ${getBusColor(
                              stop.busId
                            )}`}
                          >
                            {stop.busNumber}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Map Preview Placeholder */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Route Preview</h3>
              <Card className="p-6 h-[500px] flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <MapIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Route Map Preview</p>
                  <p className="text-sm text-gray-500">
                    Visual route map will be displayed here
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="manual" className="space-y-4 mt-6">
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Drag and drop</strong> stops to reorder them manually. This allows you to
              optimize the route based on location and traffic patterns.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Draggable Stops List */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">Reorder Stops ({routeStops.length})</h3>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {routeStops.map((stop, index) => (
                  <Card
                    key={stop.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`p-4 cursor-move transition-all ${
                      draggedItem === index ? 'opacity-50 scale-95' : 'hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <GripVertical className="h-5 w-5 text-gray-400 mt-1" />
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-sm font-semibold text-gray-700">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <MapPin className="h-4 w-4 text-gray-500" />
                          <h4 className="font-medium text-gray-900">{stop.name}</h4>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {stop.studentCount} {stop.studentCount === 1 ? 'student' : 'students'}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white ${getBusColor(
                              stop.busId
                            )}`}
                          >
                            {stop.busNumber}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Map Preview Placeholder */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Route Preview</h3>
              <Card className="p-6 h-[500px] flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <MapIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Route Map Preview</p>
                  <p className="text-sm text-gray-500">
                    Visual route map will be displayed here
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Summary */}
      <Card className="p-4 bg-green-50 border-green-200">
        <h4 className="font-semibold text-gray-900 mb-2">Route Summary</h4>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Total Stops</p>
            <p className="text-lg font-semibold text-gray-900">{routeStops.length}</p>
          </div>
          <div>
            <p className="text-gray-600">Total Students</p>
            <p className="text-lg font-semibold text-gray-900">{totalStudents}</p>
          </div>
          <div>
            <p className="text-gray-600">Buses Merged</p>
            <p className="text-lg font-semibold text-gray-900">{safeWizardData.sourceBuses?.length || 0}</p>
          </div>
        </div>
      </Card>

      {/* Bus Legend */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg">
        <span className="text-sm font-medium text-gray-700">Original Buses:</span>
        {safeWizardData.sourceBuses?.map((bus) => (
          <div key={bus.id} className="flex items-center gap-2">
            <span className={`w-4 h-4 rounded ${getBusColor(bus.id)}`}></span>
            <span className="text-sm text-gray-600">{bus.busNumber}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Step6RouteOrder;
